import Loader from 'components/Loader';
import {isEqual, orderBy} from 'lodash';
import PropTypes from 'prop-types';
import React, {Component} from 'react';
import {Col, Row} from 'react-bootstrap';
import Paginator from 'react-pagify';
import pagifyBootstrapPreset from 'react-pagify-preset-bootstrap';
import * as Table from 'reactabular-table';
import {compose} from 'redux';
import * as search from 'searchtabular';
import segmentize from 'segmentize';
import * as sort from 'sortabular';
import {buildColumns, paginate} from 'libs/datatable';
import './styles.scss';

export default class DataTablePaginated extends Component {
  constructor(props) {
    super(props);

    this.state = {
      query: {},
      rows: props.rows,
      updatedRows: [],
      columns: [],
      searchColumn: 'all',
      sortingColumns: props.sortingColumn,
      pagination: {
        page: props.page,
        perPage: props.perPage,
        totalPages: props.totalPages,
        totalCount: props.totalCount,
      },
    };

    this._onSelect = this._onSelect.bind(this);
    this._buildColumns = buildColumns.bind(this);
    this._updateSearchQuery = this._updateSearchQuery.bind(this);
    this._scrollDataTableIntoView = this._scrollDataTableIntoView.bind(this);
  }

  componentDidMount() {
    this._buildColumns(this.props.columns, true);
    this.handleRowsUpdated();
  }

  componentDidUpdate(prevProps) {
    this.handleRowsUpdated();

    if (prevProps === this.props) {
      return;
    }

    const {
      isFetching,
      rows,
      columns,
      sortHandler,
      sortingColumn,
      page,
      totalCount,
      totalPages,
      perPage,
      paginationHandler,
      shouldResetPage = true,
    } = this.props;

    const {pagination} = this.state;
    const {amount} = paginate(pagination)(rows);

    if (!isFetching) {
      if (sortHandler) {
        this.setState({sortingColumns: sortingColumn});
      }

      // Reset to page 1 if received new row data via filter, sort, or new search
      let currentPage = (pagination.page > amount || (!isEqual(rows, this.state.rows) && shouldResetPage)) ? 1 : pagination.page;

      // Only read page prop if there is paginationHandler set
      if (paginationHandler && page) {
        // use new total pages if table was updated with new amount
        if (totalPages !== 0 && totalPages !== amount) {
          currentPage = (page > totalPages) ? 1 : page;
        } else {
          currentPage = (page > amount) ? 1 : page;
        }
      }

      this.setState({
        rows,
        columns,
        pagination: {
          ...pagination,
          page: currentPage,
          totalCount,
          totalPages,
          perPage,
        },
      }, () => this._buildColumns(columns, true));
    }
  }

  _scrollDataTableIntoView() {
    this.table.scrollIntoView({block: 'start', behavior: 'smooth'});
  }

  _onSelect(page, e) {
    e.preventDefault();

    if (this.props.paginationHandler) {
      this.props.paginationHandler(page);
      this._scrollDataTableIntoView();
    } else {
      const pages = Math.ceil(
        this.state.rows.length / this.state.pagination.perPage,
      );

      this.setState({
        pagination: {
          ...this.state.pagination,
          page: Math.min(Math.max(page, 1), pages),
        },
      }, () => this._scrollDataTableIntoView());
    }
  }

  _updateSearchQuery(query) {
    const {pagination} = this.state;

    this.setState({
      query,
      pagination: {
        ...pagination,
        page: 1,
      },
    });
  }

  getPaginated() {
    const {
      sortHandler,
    } = this.props;

    const {
      query,
      rows,
      columns,
      pagination,
      sortingColumns,
    } = this.state;

    let rowsPinnedToTop = [];
    const paginated = compose(
      paginate(pagination),
      (rows = []) => {
        return [...rowsPinnedToTop, ...rows];
      },
      sort.sorter({
        columns,
        sortingColumns,
        sort: sortHandler ? (rows) => rows : orderBy,
        strategy: sort.strategies.byProperty,
      }),
      (rows = []) => {
        return rows.filter((row) => {
          if (row.keepAtTop) {
            rowsPinnedToTop.push(row);
          }
          return !row.keepAtTop;
        });
      },
      search.multipleColumns({
        columns,
        query,
      }),
    )(rows);

    return paginated;
  }

  handleRowsUpdated() {
    const {
      rowsUpdatedHandler,
    } = this.props;
    if (rowsUpdatedHandler) {
      const {updatedRows} = this.state;
      const {rows} = this.getPaginated();
      if (!isEqual(rows, updatedRows)) {
        this.setState({updatedRows: rows}, () => {
          rowsUpdatedHandler(rows);
        });
      }
    }
  }

  render() {
    const {
      tableFooter,
      isFetching,
      fetchingMessage,
      filterAvailable,
      searchLabel,
      loaderOn,
      responsive,
      tallTable,
      centerPagination,
      onRow,
      rowKey,
    } = this.props;

    const {
      searchColumn,
      query,
      rows,
      columns,
      pagination,
    } = this.state;

    const paginated = this.getPaginated();
    return (
      <div data-component={'DataTablePaginated'} ref={(node) => this.table = node}>
        {filterAvailable &&
          <Row className={'search-container'}>
            <Col xs={6} className={'pull-right'}>
              <div className="filter-container">
                <label>{searchLabel}</label>
                <search.Field
                  column={searchColumn}
                  query={query}
                  columns={columns}
                  rows={rows}
                  onChange={this._updateSearchQuery}
                />
              </div>
            </Col>
          </Row>
        }

        <Row>
          {(paginated.count > 0) &&
            <div className={`
            ${responsive ? 'table-responsive' : ''}
            ${tallTable ? 'layout__scroll-wrapper--tall-table' : ''}`}>
              <Table.Provider
                className={'table table-striped'}
                columns={columns}
              >
                <Table.Header />
                <Table.Body rows={paginated.rows} rowKey={rowKey} onRow={onRow}/>
                {tableFooter}
              </Table.Provider>
            </div>
          }

          {((paginated.count < 1) && !isFetching) &&
            <p className={'no-results alert alert-warning'}>No matching records found.</p>
          }
        </Row>

        <Row>
          {(!centerPagination && paginated.count > 0) &&
            <Col sm={4}>
              <div className={'text-left total'}>
                Showing {paginated.sliceStart} to {paginated.sliceEnd} of {paginated.count} entries
              </div>
            </Col>
          }

          {(paginated.amount > 1) &&
            <Col sm={centerPagination ? 12 : 8}>
              <div className={centerPagination ? 'text-center' : 'text-right'}>
                <Paginator.Context
                  {...pagifyBootstrapPreset}
                  segments={segmentize({
                    page: pagination.page,
                    pages: paginated.amount,
                    beginPages: 1,
                    endPages: 1,
                    sidePages: 5,
                  })}
                  onSelect={this._onSelect}
                >
                  {(pagination.page > 1) &&
                    <Paginator.Button id={'previous-page'} page={pagination.page - 1}>❮ Previous</Paginator.Button>
                  }
                  <Paginator.Segment field={'beginPages'} />
                  <Paginator.Ellipsis previousField={'beginPages'} nextField={'previousPages'} />
                  <Paginator.Segment field={'previousPages'} />
                  <Paginator.Segment field={'centerPage'} className={'active'} />
                  <Paginator.Segment field={'nextPages'} />
                  <Paginator.Ellipsis previousField={'nextPages'} nextField={'endPages'} />
                  <Paginator.Segment field={'endPages'} />
                  {(pagination.page < paginated.amount) &&
                    <Paginator.Button id={'next-page'} page={pagination.page + 1}>Next ❯</Paginator.Button>
                  }
                </Paginator.Context>
              </div>
            </Col>
          }
        </Row>

        {isFetching && loaderOn &&
          <div className={'loading-overlay'}>
            <Loader message={fetchingMessage} />
          </div>
        }
      </div>
    );
  }
}

DataTablePaginated.defaultProps = {
  tableFooter: <tfoot></tfoot>,
  loaderOn: true,
  searchLabel: 'Search:',
  isFetching: false,
  fetchingMessage: '',
  paginationHandler: false,
  allCellsTransform: false,
  centerPagination: false,
  sortHandler: false,
  page: 1,
  sortingColumn: {},
  totalPages: 0,
  totalCount: 0,
  tallTable: false,
  perPage: 50,
  rowKey: 'id',
  rowsUpdatedHandler: false,
};

DataTablePaginated.propTypes = {
  columns: PropTypes.array,
  filterAvailable: PropTypes.bool,
  isFetching: PropTypes.bool,
  fetchingMessage: PropTypes.string,
  loaderOn: PropTypes.bool,
  responsive: PropTypes.bool,
  tallTable: PropTypes.bool,
  perPage: PropTypes.number,
  rows: PropTypes.array,
  searchLabel: PropTypes.string,
  tableFooter: PropTypes.element,
  saveEdit: PropTypes.func,
  shouldResetPage: PropTypes.bool,
  centerPagination: PropTypes.bool,
  paginationHandler: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.bool,
  ]),
  allCellsTransform: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.bool,
  ]),
  sortHandler: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.bool,
  ]),
  sortingColumn: PropTypes.object,
  totalPages: PropTypes.number,
  totalCount: PropTypes.number,
  page: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.bool,
  ]),
  onRow: PropTypes.func,
  rowKey: PropTypes.string,
  rowsUpdatedHandler: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.bool,
  ]),
};
